import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useFormContext } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { BodyBlockId, getBaseBlockType } from 'constants/bodyBlocks';
import { StoreBodyBlock } from 'constants/enums';
import { motion } from 'framer-motion';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import {
  ETemplateStudioSidebarBlock,
  TemplateStudioBlock
} from 'state/template-studio.state.service';

import { cn } from 'components/lib/utils';
import {
  getMultiInstanceDescriptor,
  isMultiInstanceBlock
} from 'components/TemplatesStudio/multiInstanceBlocks';
import type { IFormStoreBlock } from 'components/TemplatesStudio/multiInstanceBlocks';
import { checkNewDesignByBlock } from 'components/TemplatesStudio/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from 'components/ui/collapsible';
import { SidebarMenuItem } from 'components/ui/sidebar';

import { mapBlockToSecondarySidebar } from '../../../utils/blockForms.utils';

import {
  BlockMenuItemDetachButton,
  BlockMenuItemRemoveButton,
  BlockMenuItemVisibilityButton
} from './blockMenuItemActions';

export type BlockMenuId = TemplateStudioBlock | BodyBlockId;

const isMultiInstanceInstanceId = (blockType: BlockMenuId): boolean => {
  const baseBlock = getBaseBlockType(blockType as BodyBlockId);
  return baseBlock !== blockType && isMultiInstanceBlock(baseBlock);
};

const getInstanceSuffix = (
  instanceId: string,
  baseBlock: StoreBodyBlock
): number => Number(instanceId.slice(baseBlock.length + 1));

const TEMPLATE_STUDIO_STATIC_BLOCKS = {
  HEADER: ETemplateStudioSidebarBlock.HEADER,
  FOOTER: ETemplateStudioSidebarBlock.FOOTER
};

export const mapBlockToTitle: Record<TemplateStudioBlock, string> = {
  [StoreBodyBlock.banners]: 'Banners',
  [StoreBodyBlock.bundles]: 'Bundles',
  [StoreBodyBlock.specialOffers]: 'Promotions',
  [StoreBodyBlock.rollingOffers]: 'Rolling Offers',
  [StoreBodyBlock.onePlusMany]: '1+Many',
  [StoreBodyBlock.richText]: 'Rich Text',
  [StoreBodyBlock.dailyBonusBadge]: 'Daily Bonus',
  [StoreBodyBlock.playerLevel]: 'Player Level',
  [StoreBodyBlock.progressBars]: 'Progress Bars',
  [StoreBodyBlock.rewardCalendar]: 'Reward Calendar',
  [StoreBodyBlock.triggeredPopups]: 'Triggered Popups',
  [ETemplateStudioSidebarBlock.POPUP]: 'Popup',
  [ETemplateStudioSidebarBlock.TRIGGERED_POPUPS_POPUP]: 'Triggered Popup',
  [ETemplateStudioSidebarBlock.REWARD_CALENDAR_POPUP]: 'Reward Calendar Popup',
  [ETemplateStudioSidebarBlock.FOOTER]: 'Footer',
  [ETemplateStudioSidebarBlock.HEADER]: 'Header'
};

export const getBlockTitle = (blockType: BlockMenuId): string => {
  const baseBlock = getBaseBlockType(blockType as BodyBlockId);
  const descriptor = getMultiInstanceDescriptor(baseBlock);
  if (descriptor !== undefined && baseBlock !== blockType) {
    const suffix = getInstanceSuffix(blockType as string, baseBlock);
    return `${descriptor.modalRow.sectionLabel} #${suffix}`;
  }
  return mapBlockToTitle[blockType as TemplateStudioBlock];
};

const mapBlockToDesignIdPath: Partial<Record<TemplateStudioBlock, string>> = {
  [StoreBodyBlock.bundles]: 'bundle.selectedDesignId',
  [StoreBodyBlock.specialOffers]: 'promotion.selectedDesignId',
  [StoreBodyBlock.rollingOffers]: 'rollingOffers.selectedDesignId'
};

const resolveInitialDesignId = (
  blockType: BlockMenuId,
  getValues: (path: string) => unknown
): string | undefined => {
  if (isMultiInstanceInstanceId(blockType)) {
    const storeBlocks = getValues('storeBlocks') as
      | { id?: string; instanceKey?: string; selectedDesignId?: string }[]
      | undefined;
    const instance = Array.isArray(storeBlocks)
      ? storeBlocks.find(
          (block) =>
            block.id === blockType || block.instanceKey === blockType
        )
      : undefined;
    return instance?.selectedDesignId;
  }
  const designIdPath = mapBlockToDesignIdPath[blockType as TemplateStudioBlock];
  if (!designIdPath) return undefined;
  return getValues(designIdPath) as string | undefined;
};

const NewBadge: React.FC = () => (
  <div
    className={cn(
      'new-category-badge flex ml-2 items-center px-2 py-1 text-xs rounded-full bg-base-secondary w-[47px] h-[20px] justify-center align-middle font-semibold'
    )}
  >
    <span className={cn('text-green-500')}>{'New'}</span>
  </div>
);

interface IDesignBlockMenuItemProps {
  className?: string;
  blockType: BlockMenuId;
  isHidden?: boolean;
  children?: React.ReactNode;
  dragIndex?: number;
  isDragging?: boolean;
  isCurrentItemDragged?: boolean;
  dragHandleListeners?: Record<string, unknown>;
  dragHandleAttributes?: Record<string, unknown>;
  isMergedSubBlock?: boolean;
  onDetach?: () => void;
  onRemove?: () => void;
  onToggleVisibility?: (event: React.MouseEvent) => void;
  v2ItemId?: string;
}

export const DesignBlockMenuItem: React.FC<IDesignBlockMenuItemProps> = ({
  className,
  blockType,
  isHidden = false,
  children,
  isDragging = false,
  isCurrentItemDragged = false,
  dragHandleListeners,
  dragHandleAttributes,
  isMergedSubBlock = false,
  onDetach,
  onRemove,
  onToggleVisibility,
  v2ItemId
}) => {
  const {
    openSidebarBlock,
    openSidebarMultiInstanceBlockId,
    setOpenSidebarBlock,
    setOpenSidebarMultiInstanceBlockId,
    setOpenSecondarySidebarBlock,
    setScrollToSection,
    resetSidebars
  } = useTemplateStudioState();

  const { watch, getValues } = useFormContext();
  const watchedStoreBlocks = watch('storeBlocks');
  const visibleStoreBlockCount = useMemo(
    () =>
      ((watchedStoreBlocks || []) as IFormStoreBlock[]).filter(
        (block) => block.isVisible
      ).length,
    [watchedStoreBlocks]
  );

  const baseBlockType = getBaseBlockType(blockType as BodyBlockId);
  const isMultiInstanceInstance =
    baseBlockType !== blockType && isMultiInstanceBlock(baseBlockType);
  const baseBlock: TemplateStudioBlock = isMultiInstanceInstance
    ? baseBlockType
    : (blockType as TemplateStudioBlock);

  const [selectedInitialDesignId, setSelectedInitialDesignId] = useState<
    string | undefined
  >(undefined);

  const shouldOpenSecondarySidebar = useMemo(
    () => !!mapBlockToSecondarySidebar[baseBlock],
    [baseBlock]
  );

  useEffect(() => {
    const value = resolveInitialDesignId(blockType, getValues);
    if (value !== undefined) {
      setSelectedInitialDesignId(value);
    }
  }, [blockType]);

  const isOpen = useMemo((): boolean => {
    if (openSidebarBlock !== baseBlock) return false;
    if (!isMultiInstanceInstance) return true;
    return openSidebarMultiInstanceBlockId === blockType;
  }, [
    openSidebarBlock,
    baseBlock,
    isMultiInstanceInstance,
    blockType,
    openSidebarMultiInstanceBlockId
  ]);

  const hasNewDesignInTheme = useMemo(
    () =>
      selectedInitialDesignId &&
      checkNewDesignByBlock[baseBlock] &&
      checkNewDesignByBlock[baseBlock]!(selectedInitialDesignId),
    [baseBlock, selectedInitialDesignId]
  );

  const isStoreBlock = useMemo((): boolean => {
    if (isMultiInstanceInstance) return true;
    return (Object.values(StoreBodyBlock) as string[]).includes(
      blockType as string
    );
  }, [isMultiInstanceInstance, blockType]);

  const isDraggableBlock: boolean = isStoreBlock;
  const isBlockVisible = !isStoreBlock || !isHidden;

  const isLastVisibleBlock = useMemo(
    (): boolean =>
      isStoreBlock && isBlockVisible && visibleStoreBlockCount === 1,
    [isStoreBlock, isBlockVisible, visibleStoreBlockCount]
  );

  const hasForm = React.Children.count(children) > 0;

  const hasSecondarySidebar = useMemo(
    () => mapBlockToSecondarySidebar[baseBlock],
    [baseBlock]
  );

  const isStaticMenuItem = useMemo(
    (): boolean => !hasSecondarySidebar && !hasForm,
    [hasSecondarySidebar, hasForm]
  );

  const getScrollSection = useCallback(
    (shouldScroll: boolean): StoreBodyBlock | string | undefined => {
      if (!shouldScroll) return undefined;
      if (isStoreBlock) {
        return v2ItemId === undefined ? (blockType as string) : v2ItemId;
      }
      if (
        baseBlock === TEMPLATE_STUDIO_STATIC_BLOCKS.HEADER ||
        baseBlock === TEMPLATE_STUDIO_STATIC_BLOCKS.FOOTER
      ) {
        return baseBlock;
      }
      return undefined;
    },
    [isStoreBlock, v2ItemId, blockType, baseBlock]
  );

  const handleOpenChange = useCallback(
    (nextIsOpen: boolean): void => {
      if (isStaticMenuItem) {
        if (isBlockVisible) {
          setScrollToSection(getScrollSection(true));
        }
        resetSidebars();
        return;
      }

      if (!isBlockVisible) {
        setOpenSidebarBlock(undefined);
        return;
      }

      if (nextIsOpen) {
        setOpenSidebarBlock(baseBlock);
        setOpenSidebarMultiInstanceBlockId(
          isMultiInstanceInstance ? (blockType as string) : undefined
        );
        if (shouldOpenSecondarySidebar) {
          setOpenSecondarySidebarBlock(baseBlock);
        } else {
          setOpenSecondarySidebarBlock(undefined);
        }
      } else {
        resetSidebars();
      }

      setScrollToSection(getScrollSection(nextIsOpen));
    },
    [
      isStaticMenuItem,
      isBlockVisible,
      getScrollSection,
      setScrollToSection,
      resetSidebars,
      setOpenSidebarBlock,
      baseBlock,
      isMultiInstanceInstance,
      blockType,
      setOpenSidebarMultiInstanceBlockId,
      shouldOpenSecondarySidebar,
      setOpenSecondarySidebarBlock
    ]
  );

  const collapseIcon = useMemo((): string => {
    if (isOpen && hasForm) return 'fa-chevron-down';
    return 'fa-chevron-right';
  }, [isOpen, hasForm]);

  const removeButtonTitle = useMemo((): string => {
    const descriptor = getMultiInstanceDescriptor(baseBlockType);
    const label = descriptor ? descriptor.modalRow.sectionLabel : 'block';
    return `Remove ${label} instance`;
  }, [baseBlockType]);

  const title = useMemo((): string => getBlockTitle(blockType), [blockType]);

  const dragHandleProps = useMemo(
    () =>
      isDraggableBlock
        ? { ...dragHandleListeners, ...dragHandleAttributes }
        : {},
    [isDraggableBlock, dragHandleListeners, dragHandleAttributes]
  );

  const gripOpacityClassName = useMemo((): string => {
    const isGripHoverRevealed =
      !isMergedSubBlock &&
      isDraggableBlock &&
      !(isDragging && !isCurrentItemDragged);
    if (isGripHoverRevealed) {
      return 'opacity-0 group-hover/collapsible:opacity-100';
    }
    return 'opacity-0';
  }, [isMergedSubBlock, isDraggableBlock, isDragging, isCurrentItemDragged]);

  const menuActions = useMemo<
    { key: string; isVisible: boolean; element: React.ReactNode }[]
  >(
    () => [
      {
        key: 'detach',
        isVisible: isMergedSubBlock && Boolean(onDetach),
        element: onDetach && (
          <BlockMenuItemDetachButton
            blockType={blockType as string}
            onDetach={onDetach}
          />
        )
      },
      {
        key: 'visibility',
        isVisible:
          !isMergedSubBlock && isStoreBlock && Boolean(onToggleVisibility),
        element: onToggleVisibility && (
          <BlockMenuItemVisibilityButton
            blockType={blockType as string}
            isVisible={isBlockVisible}
            isDisabled={isLastVisibleBlock}
            onToggle={onToggleVisibility}
          />
        )
      },
      {
        key: 'remove',
        isVisible: !isMergedSubBlock && Boolean(onRemove),
        element: onRemove && (
          <BlockMenuItemRemoveButton
            blockType={blockType as string}
            title={removeButtonTitle}
            onRemove={onRemove}
          />
        )
      }
    ],
    [
      isMergedSubBlock,
      onDetach,
      isStoreBlock,
      blockType,
      isBlockVisible,
      isLastVisibleBlock,
      onToggleVisibility,
      onRemove,
      removeButtonTitle
    ]
  );

  return (
    <Collapsible
      className={cn(
        'group/collapsible',
        'py-2',
        className,
        isMergedSubBlock && isOpen && 'bg-white rounded-lg'
      )}
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <SidebarMenuItem className="flex flex-col">
        <CollapsibleTrigger
          className="flex justify-between items-center w-full text-base-mutedForeground text-sm"
          data-testid={`TemplatesStudio-BlockMenu-${blockType}`}
        >
          <div
            className={cn(
              'leading-5 font-sans text-base-mutedForeground flex items-center gap-1',
              isOpen && 'text-indigo-600 font-semibold'
            )}
          >
            <span
              {...dragHandleProps}
              className={cn(
                'inline-flex items-center',
                isDraggableBlock ? 'cursor-grab active:cursor-grabbing' : ''
              )}
            >
              <FontAwesomeIcon
                icon={`fa-solid fa-grip-dots-vertical`}
                className={cn(
                  'text-xs transition-opacity w-[12px] text-base-mutedForeground',
                  gripOpacityClassName
                )}
              />
            </span>
            {title}
            {hasNewDesignInTheme && <NewBadge />}
          </div>
          <div className="flex items-center gap-2">
            {menuActions
              .filter((action) => action.isVisible)
              .map((action) => (
                <React.Fragment key={action.key}>
                  {action.element}
                </React.Fragment>
              ))}
            {!isStaticMenuItem && (
              <FontAwesomeIcon
                icon={`fa-solid ${collapseIcon}`}
                className={cn(
                  'text-xs text-base-mutedForeground',
                  isOpen && 'text-indigo-600'
                )}
              />
            )}
          </div>
        </CollapsibleTrigger>
        {hasForm && (
          <motion.div
            layout
            initial={{ height: 0 }}
            animate={{ height: isOpen ? 'auto' : 0 }}
            transition={{ duration: 0.1, ease: 'easeInOut' }}
          >
            <CollapsibleContent className="flex flex-col gap-2 my-2 mr-2 ml-4">
              {children}
            </CollapsibleContent>
          </motion.div>
        )}
      </SidebarMenuItem>
    </Collapsible>
  );
};
